import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from '../api/axios';

type AuthContextType = {
  token: string | null;
  user: any;
  isAuthLoading: boolean;
  login: (token: string, user: any) => void;
  logout: () => void;
  setUser: (user: any) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("auth_token");

      if (!storedToken) {
        setIsAuthLoading(false);
        return;
      }

      setToken(storedToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;

      try {
        const res = await axios.get("me");
        setUser(res.data.user ?? res.data.data);
      } catch (err) {
        console.error("❌ gagal fetch user", err);
        localStorage.removeItem("auth_token");
        setToken(null);
        setUser(null);
      } finally {
        setIsAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = (newToken: string, userData: any) => {
    localStorage.setItem("auth_token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    const normalizedUser = {
      ...userData,
      id: userData.account_id,
    };
    setToken(newToken);
    setUser(normalizedUser);
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
        token,
        user,
        login,
        logout,
        setUser,
        isAuthLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
